import AddIcon from "@mui/icons-material/Add";
import { styled } from "@mui/material/styles";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import { grey } from "designSystem/styles/color";
import { ColorSpec } from "designSystem/components/colorchip/ColorChipArrayStore";
import { ButtonEnum } from "designSystem/types/button-types";
import { CssGridColumnByWidth } from "designSystem/components/cssGrid/CssGrid";
import { useHover } from "designSystem/hooks";
import {
	Box,
	Button,
	ColorChipArray,
	ConfirmDialog,
	CsiImage,
	DataGridCardProps,
	FlexColumn,
	Link,
	ListItem,
	ListWithSubheader,
	MenuItem,
	MoreHorizMenu,
	OverflowText,
	PopupDialog,
	Text
} from "designSystem/index";
import { ListSection } from "designSystem/components/list/ListWithSubheader";
import { useLocale } from "designSystem/locale";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import locale from "csi/locale";
import DojoGalleryAttributes from "csi/page/DojoGalleryAttributes";
import DojoView from "csi/page/DojoView";
import session from "csi/session";
import { C8Node, cnl } from "csi/typings";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent, useRef, useState } from "react";
import { usePresentationPageStore } from "../main";
import { usePPProductLayoutStore } from "../PPProductLayoutStore";
import { getProductAttributeSpecs, getProductUseCountInPresentation } from "../PPService";
import PPInvalidTag from "./PPInvalidTag";
import { useProductGalleryStore } from "./ProductGalleryStore";

const PREFIX = "PPProductCard";

export const classes = {
	invalidation: `${PREFIX}invalidation`,
	menu: `${PREFIX}menu`,
	root: `${PREFIX}root`,
};

export const StyledFlexColumn = styled(FlexColumn)((
	{
		theme,
	},
) => ({
	[`& .${classes.invalidation}`]: {
		position: "absolute",
		left: 0,
		top: theme.spacing(3),
	},

	[`& .${classes.menu}`]: {
		position: "absolute",
		right: theme.spacing(3),
		top: theme.spacing(3),
	},

	[`&.${classes.root}`]: {
		border: STANDARD_BORDER_STYLE,
		borderRadius: 3,
		position: "relative",
	},
}));

export const COLUMNS_BY_WIDTH: CssGridColumnByWidth[] = [
	[2, 1],
	[3, 730],
	[4, 986],
	[6, 1441],
];

export const OPTIONS = "Options";
const USED_IN = "UsedIn";

export const StyledBox = styled(Box)((
	{
		theme,
	},
) => ({
	[`& .${classes.invalidation}`]: {
		position: "absolute",
		left: 0,
		top: theme.spacing(3),
	},

	[`& .${classes.menu}`]: {
		position: "absolute",
		right: theme.spacing(3),
		top: theme.spacing(3),
	},

	[`&.${classes.root}`]: {
		border: STANDARD_BORDER_STYLE,
		borderRadius: 3,
		position: "relative",
	},
}));

export const PPProductCardSkeleton = () => {
	return (
		<StyledBox className={classes.root}>
			<Skeleton height={200} variant="rectangular" />
			<Box m={3}>
				<Skeleton height={16} />
				<Skeleton height={22} />
			</Box>
		</StyledBox>
	);
};

export interface PPProductCardProps extends DataGridCardProps<C8Node> {
	/** selected color to display */
	colorUrl?: cnl;

	/** indicate if need to allow to switch through images */
	disableSwitchNodeImages?: boolean;

	/** indicate if need to show the more menu */
	hideMoreMenu?: boolean;

	/** indicate if needs to show UsedIn section */
	hideUsedInSection?: boolean;
}

export const PPProductCard: FunctionComponent<PPProductCardProps> = observer((props) => {
	const { colorUrl, disableSwitchNodeImages, hideMoreMenu, hideUsedInSection, item } = props;
	const [useCount, setUseCount] = useState(0);
	const [viewInfo, setViewInfo] = useState(false);
	const loc = useLocale();
	const productLayoutStore = usePPProductLayoutStore();
	const productGalleryStore = useProductGalleryStore();

	// in the case of presentation creation, the page store would be null
	const { violationUrlsOfProduct, isPresentationEditor, recalculateViolations } = usePresentationPageStore() || {};

	const productHasViolation = violationUrlsOfProduct?.has(item.$URL);
	const hoverRef = useRef(null);
	const hovering = useHover(hoverRef);

	const {
		cache,
		getColorSpecs,
		getDefaultColorSpec,
		getImageNode,
		onAddToSet,
		onOpenGalleryCardMenu,
		onRemove,
		presentationUrl,
		setLists,
		showAttributes,
		supportsColor,
	} = useProductGalleryStore();

	const attributeSpecs = getProductAttributeSpecs(item);
	const colors = getColorSpecs(item, productHasViolation, colorUrl);
	const colorHasViolation = colors.some((color) => color.hasViolation);
	const defaultColor = getDefaultColorSpec(item, productHasViolation, colors, colorUrl);
	const [color, setColor] = useState<ColorSpec>(defaultColor);
	const hasColor = supportsColor(item);
	const imageNode = getImageNode(color || defaultColor || colors[0]);
	const typeName = loc.type(item.$Type);
	const locationUrl = session.getLocationForUrl(item.$URL);

	const selectedColor = color || colors[0];
	const showAsDisabled = hasColor ? selectedColor?.hasViolation : productHasViolation;

	const onClickAddToSet = () => {
		onAddToSet(item.$URL, color?.id);
	};

	const onClickColor = (color: ColorSpec) => {
		setColor(color);
	};

	const onClickRemove = async () => {
		const count = await getProductUseCountInPresentation(item, presentationUrl);

		if (count) {
			setUseCount(count);
		} else {
			onConfirmRemove();
		}
	};

	const onClickViewInfo = () => {
		setViewInfo(true);
	};

	const onCloseViewInfo = () => {
		setViewInfo(false);
	};

	const onConfirmClose = () => {
		setUseCount(0);
	};

	const onConfirmRemove = async (): Promise<void> => {
		await onRemove(item.$URL);
		recalculateViolations();
	};

	const onClickUsedSet = (setUrl: cnl): void => {
		if (productLayoutStore?.fixedEditSetStore) {
			// in the case of edit set dialog, just open new window
			window.open(session.getLocationForUrl(setUrl));
		} else if (productLayoutStore) {
			// in the case of product gallery in products tab, open the right pane if has privilege
			if (isPresentationEditor) {
				productLayoutStore.onAddToSet(item.$URL, colorUrl, true);
				productLayoutStore.setsStore.selectSet(setUrl, true);
			} else {
				window.open(session.getLocationForUrl(setUrl));
			}
		} else if (productGalleryStore) {
			// in the case of set page, just call back, up to the function itself
			productGalleryStore.editSetCallback(setUrl);
		}
	};

	// generate the menuItems for the gallery card
	const setSection: ListSection = {
		sectionName: loc.resource(USED_IN),
		sectionBody: (
			<>
				{setLists?.map(({ Name, $ }) => (
					<MenuItem
						key={$}
						onClick={onClickUsedSet.bind(this, $)}
						text={Name || loc.resource("NAME_EMPTY")}
						tooltipProps={{ placement: "right" }}
					/>
				),
				)}
			</>
		),
	};

	const optionsSection: ListSection = {
		sectionName: loc.resource(OPTIONS),
		sectionBody: (
			<>
				<ListItem design="list" onClick={onClickViewInfo}>
					<ListItemText primary={loc.resource("ViewInfo")} />
				</ListItem>
				{onRemove && (
					<ListItem
						design="list"
						disabled={!!productLayoutStore?.editSetStore}
						onClick={onClickRemove}
					>
						<ListItemText primary={loc.resource("Remove")} />
					</ListItem>
				)}
			</>
		),
	};

	let sections;
	if (hideUsedInSection) {
		sections = [optionsSection];
	} else {
		sections = [optionsSection, setSection];
	}

	const showInvalidTagOnCard = (productHasViolation || colorHasViolation);

	return (
		<StyledFlexColumn className={classes.root} {... { ref: hoverRef }}>
			<CsiImage
				allowSwitchNodeImages={!disableSwitchNodeImages}
				aspectRatio={1}
				displayControls={hovering}
				noImageComponent={(
					<Text
						lineHeight={16}
						res="NoImageAvailable"
						semiBold
						size={14}
						style={{ color: grey[800] }}
					/>
				)}
				node={imageNode}
			/>
			<Box p={3}>
				<OverflowText
					bold
					lineHeight={16}
					size={14}
					text={item.$Name}
				/>
			</Box>
			<Box flexGrow={1}>
				{hasColor && (
					<Box pb={3} px={3}>
						<ColorChipArray
							colors={colors}
							defaultColor={defaultColor}
							onSelectChip={onClickColor}
							value={color}
						/>
					</Box>
				)}
				{attributeSpecs && showAttributes && (
					<Box pb={3} px={3}>
						<DojoGalleryAttributes
							cache={cache}
							node={item}
							showLabels
							specs={attributeSpecs}
						/>
					</Box>
				)}
			</Box>
			{onAddToSet && isPresentationEditor && (
				<Box pb={3} px={3}>
					{hovering ? (
						<Button
							design="primary"
							disabled={showAsDisabled}
							fullWidth
							onClick={onClickAddToSet}
							res="PPAddToSet"
							startIcon={<AddIcon />}
						/>
					)
					// need to wrap this button with another container so that the "currentColor" won't affect the styling
						: (
							<Box>
								<Button
									design="outline"
									disabled={showAsDisabled}
									fullWidth
									res="PPAddToSet"
									startIcon={<AddIcon />}
								/>
							</Box>
						)}
				</Box>
			)}
			{!hideMoreMenu && (
				<MoreHorizMenu
					className={classes.menu}
					design="outline"
					hovering={hovering}
					onOpen={onOpenGalleryCardMenu.bind(this, item)}
					round
					shadow
					showOnHover
				>
					<ListWithSubheader sections={sections} style={{ minWidth: 70, maxWidth: 216 }} />
				</MoreHorizMenu>
			)}
			{showInvalidTagOnCard && (
				<Box className={classes.invalidation}>
					<PPInvalidTag res="PresentationItemInvalidTag" />
				</Box>
			)}
			{viewInfo && (
				<PopupDialog
					disableEnforceFocus
					fullHeight
					minWidth={1290}
					onClose={onCloseViewInfo}
					open
					showClose
					title={locale.getNodeConfiguredName(item, cache)}
				>
					{{
						main: (
							<DojoView
								forceReadOnly
								nodeUrl={item.$URL}
								viewId="Properties"
								viewParams={{ bPopup: true }}
							/>
						),
						buttons: (
							<Fragment>
								<Link
									href={locationUrl}
									target="_blank"
								>
									<Text
										res="GoTo"
										resParams={[typeName]}
									/>
								</Link>
								<Button
									design="secondary"
									onClick={onCloseViewInfo}
									res={ButtonEnum.BUTTON_CLOSE}
								/>
							</Fragment>
						),
					}}
				</PopupDialog>
			)}
			{useCount > 0 && (
				<ConfirmDialog
					category="warn"
					confirmRes="Remove"
					messageRes="PPRemoveProductConfirm"
					messageResParams={[typeName, useCount, loc.type("ProductPresentationSet", useCount > 1, true)]}
					onClose={onConfirmClose}
					onConfirm={onConfirmRemove}
					open
					titleRes="Remove_Prompt"
					titleResParams={[typeName]}
				/>
			)}
		</StyledFlexColumn>
	);
});

export default PPProductCard;